import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Scale, Shield, Clock, Award, CheckCircle, Star, ArrowRight, Briefcase, Users, FileText, Building2, ShieldCheck, TrendingUp, Globe, ChevronLeft, ChevronRight, Gavel, HandshakeIcon, Landmark } from 'lucide-react';
import ConsultationDialog from '../components/ConsultationDialog';

const HomePage = () => {
  const [isConsultationOpen, setIsConsultationOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const expertiseCards = [
    {
      icon: Building2,
      title: 'Enterprise Legal Solutions',
      description: 'Strategic counsel for modern businesses. Build robust legal foundations with cutting-edge contract management and governance frameworks.',
    },
    {
      icon: ShieldCheck,
      title: 'IP Asset Management',
      description: 'Transform ideas into protected assets. Maximize value through comprehensive trademark registration and copyright enforcement strategies.',
    },
    {
      icon: TrendingUp,
      title: 'Strategic Deal Execution',
      description: 'Power your growth through expert transaction support. Accelerate acquisitions and partnerships with decisive legal leadership.',
    },
    {
      icon: Globe,
      title: 'Global Mobility Solutions',
      description: 'Break through regulatory barriers effortlessly. Unlock international opportunities with end-to-end immigration and compliance expertise.',
    },
  ];

  const allServices = [
    {
      title: 'Corporate & Business Contracts',
      slug: 'corporate-business-contracts',
      icon: FileText,
      description: 'Precision-crafted agreements designed to mitigate risk and maximize business value.',
    },
    {
      title: 'Corporate & Business Formation',
      slug: 'corporate-business-formation',
      icon: Building2,
      description: 'Strategic entity structuring optimized for tax efficiency and liability protection.',
    },
    {
      title: 'Business Transaction',
      slug: 'business-transaction',
      icon: TrendingUp,
      description: 'Seamless deal execution for acquisitions, divestitures, and strategic partnerships.',
    },
    {
      title: 'Mergers & Acquisitions',
      slug: 'mergers-acquisitions',
      icon: Briefcase,
      description: 'End-to-end M&A advisory driving value creation and operational synergies.',
    },
    {
      title: 'Non-Profit Law',
      slug: 'non-profit-law',
      icon: Users,
      description: 'Holistic legal framework supporting mission-driven organizations and foundations.',
    },
    {
      title: 'Government Contracting',
      slug: 'government-contracting',
      icon: Landmark,
      description: 'Winning strategies for federal procurement and regulatory navigation.',
    },
    {
      title: 'Business Disputes',
      slug: 'business-disputes',
      icon: Gavel,
      description: 'Results-driven advocacy in complex commercial litigation and alternative dispute resolution.',
    },
    {
      title: 'Anti-Corruption Compliance',
      slug: 'anti-corruption-compliance',
      icon: ShieldCheck,
      description: 'Enterprise-grade FCPA compliance frameworks and anti-bribery risk mitigation.',
    },
    {
      title: 'Immigration',
      slug: 'immigration',
      icon: Globe,
      description: 'Streamlined visa processing and global talent mobility solutions.',
    },
    {
      title: 'Trademark Registration',
      slug: 'trademark-registration',
      icon: Shield,
      description: 'Comprehensive brand protection through strategic trademark portfolio management.',
    },
    {
      title: 'Copyright Registration',
      slug: 'copyright-registration',
      icon: FileText,
      description: 'Bulletproof copyright protection securing your creative intellectual assets.',
    },
  ];

  const services = [
    {
      icon: Briefcase,
      title: 'Enterprise Legal Solutions',
      description: 'Transform your business with strategic legal counsel tailored for growth-stage companies and industry leaders.',
      items: ['Entity Structuring', 'Deal Advisory', 'M&A Services', 'Risk Management'],
    },
    {
      icon: Users,
      title: 'Individual Legal Counsel',
      description: 'Personalized legal guidance helping individuals navigate life-changing decisions with confidence.',
      items: ['Visa Services', 'Wealth Planning', 'Domestic Relations', 'Injury Claims'],
    },
    {
      icon: FileText,
      title: 'Innovation Protection',
      description: 'Monetize your creativity through strategic IP protection and licensing arrangements.',
      items: ['Brand Registration', 'Content Protection', 'Patent Prosecution', 'Licensing Deals'],
    },
  ];

  const howItWorks = [
    {
      step: '01',
      title: 'Connect With Us',
      description: 'Share your legal challenge through our streamlined consultation request platform.',
    },
    {
      step: '02',
      title: 'Expert Assessment',
      description: 'Specialized attorneys analyze your situation and connect within one business day.',
    },
    {
      step: '03',
      title: 'Strategic Roadmap',
      description: 'Receive actionable legal strategy designed specifically for your objectives.',
    },
    {
      step: '04',
      title: 'Execute & Win',
      description: 'Partner with us to deliver measurable results and achieve your goals.',
    },
  ];

  const faqs = [
    {
      question: 'What investment is required for consultation?',
      answer: 'Initial strategy sessions are provided at no cost. We\'ll evaluate your legal requirements and outline transparent pricing structures during this complimentary consultation.',
    },
    {
      question: 'Which practice areas does your firm serve?',
      answer: 'Our multidisciplinary practice covers enterprise law, commercial transactions, IP portfolio management, immigration solutions, and specialized individual services. Explore our Services page for detailed capabilities.',
    },
    {
      question: 'What is your attorney response timeframe?',
      answer: 'Rapid engagement is our priority. Our legal professionals typically initiate contact within one business day following consultation submission.',
    },
    {
      question: 'Are remote consultations available?',
      answer: 'Absolutely. We provide flexible engagement through both physical office meetings and secure virtual consultation platforms.',
    },
    {
      question: 'How should I prepare for consultation?',
      answer: 'Compile relevant documentation including agreements, official correspondence, and communications. Prepare a chronological summary and list specific questions for maximum consultation efficiency.',
    },
  ];

  return (
    <div data-testid="homepage">
      <Helmet>
        <title>Zorvian Associates - Elite Legal Services | California's Premier Business Attorneys</title>
        <meta name="description" content="Zorvian Associates delivers transformative legal solutions for ambitious businesses and individuals. Our award-winning attorneys excel in corporate strategy, IP monetization, immigration excellence, and more. Schedule your complimentary consultation now." />
        <meta name="keywords" content="elite law firm, business legal counsel, corporate strategy attorney, IP monetization, immigration expertise, California premier lawyers, strategic legal advisory" />
      </Helmet>
      
      {/* Enhanced Hero Section with Background Pattern */}
      <section className="relative bg-gradient-to-br from-[#0F172A] via-[#1e293b] to-[#0F172A] py-24 lg:py-32 overflow-hidden">
        {/* Decorative background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <Award className="h-4 w-4 text-[#2563EB]" />
              <span className="text-sm font-medium text-white">Award-Winning Legal Excellence</span>
            </div>
            <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 font-heading transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              Strategic Legal Solutions That Drive Business Success
            </h1>
            <p className={`text-lg text-slate-300 mb-8 max-w-2xl mx-auto transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              Partner with elite attorneys who transform legal challenges into competitive advantages. We deliver precision counsel that protects your assets, accelerates growth, and positions your enterprise for long-term market leadership.
            </p>
            <div className={`flex flex-col sm:flex-row gap-4 justify-center transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <Button
                onClick={() => setIsConsultationOpen(true)}
                size="lg"
                className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-8 py-6 text-lg btn-primary shadow-xl shadow-blue-500/20"
                data-testid="hero-cta-button"
              >
                Get Free Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="px-8 py-6 text-lg border-2 border-white text-white hover:bg-white hover:text-[#0F172A]"
              >
                <Link to="/our-attorneys">Meet Our Attorneys</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="bg-white py-12 border-y border-slate-200">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-3 gap-4 md:gap-8">
            <div className="text-center fade-in-up">
              <div className="text-2xl md:text-3xl font-bold text-[#2563EB] mb-1 md:mb-2">500+</div>
              <div className="text-xs md:text-sm text-slate-600">Transactions Successfully Closed</div>
            </div>
            <div className="text-center fade-in-up" style={{ animationDelay: '0.1s' }}>
              <div className="text-2xl md:text-3xl font-bold text-[#2563EB] mb-1 md:mb-2">15+</div>
              <div className="text-xs md:text-sm text-slate-600">Industry-Leading Attorneys</div>
            </div>
            <div className="text-center fade-in-up" style={{ animationDelay: '0.2s' }}>
              <div className="text-2xl md:text-3xl font-bold text-[#2563EB] mb-1 md:mb-2">98%</div>
              <div className="text-xs md:text-sm text-slate-600">Client Success Rate</div>
            </div>
          </div>
        </div>
      </section>

      {/* Expert Legal Solutions Section - Side by Side Layout */}
      <section className="section-padding bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Content - Left Side */}
              <div>
                <h2 className="text-4xl font-bold text-[#0F172A] mb-6 font-heading">Innovative Legal Frameworks Engineered for Market Leaders</h2>
                <p className="text-lg text-slate-600 leading-relaxed mb-8">
                  Zorvian Associates architects sophisticated legal infrastructures that fuel business expansion and safeguard competitive positioning. 
                  Our elite counsel delivers battle-tested strategies across corporate structuring, commercial agreements, dispute resolution, and beyond, 
                  empowering decisive action with bulletproof legal protection and strategic market intelligence.
                </p>
                
                <div className="space-y-6">
                  {expertiseCards.map((card, index) => (
                    <div key={index} className="flex items-start space-x-4">
                      <div className="bg-gradient-to-br from-blue-50 to-slate-50 w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0">
                        <card.icon className="h-7 w-7 text-[#2563EB]" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-[#0F172A] mb-2 font-heading">{card.title}</h3>
                        <p className="text-sm text-slate-600 leading-relaxed">{card.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Image - Right Side */}
              <div className="relative">
                <div className="aspect-[4/3] rounded-2xl shadow-2xl overflow-hidden">
                  <img 
                    src="https://images.pexels.com/photos/159832/justice-law-case-hearing-159832.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" 
                    alt="Law books representing legal expertise and knowledge"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Slider Section */}
      <section className="section-padding bg-white overflow-hidden hidden sm:block">
        <div className="container mx-auto px-4 sm:px-4 md:px-6">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A] mb-3 sm:mb-4 font-heading">Our Legal Services</h2>
            <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto px-0 sm:px-4">
              Comprehensive legal solutions tailored to your unique needs
            </p>
          </div>
          
          {/* Slider with react-slick */}
          <div className="relative w-full mx-auto slider-container services-slider-mobile services-mobile-full-width">
            <Slider
              dots={true}
              infinite={true}
              speed={400}
              slidesToShow={3}
              slidesToScroll={1}
              arrows={true}
              centerMode={false}
              responsive={[
                {
                  breakpoint: 1024,
                  settings: {
                    slidesToShow: 3,
                    slidesToScroll: 1,
                    arrows: true,
                    dots: false
                  }
                },
                {
                  breakpoint: 768,
                  settings: {
                    slidesToShow: 2,
                    slidesToScroll: 1,
                    arrows: true,
                    dots: true
                  }
                },
                {
                  breakpoint: 640,
                  settings: {
                    slidesToShow: 2,
                    slidesToScroll: 1,
                    arrows: true,
                    dots: false,
                    infinite: true,
                    adaptiveHeight: true
                  }
                }
              ]}
            >
              {allServices.map((service, index) => (
                <div key={index} className="px-0 sm:px-3">
                  <Card className="service-card border-0 shadow-lg bg-white h-full group cursor-pointer">
                    <Link to={`/services/${service.slug}`} className="block h-full">
                      <CardContent className="p-5 sm:p-6 md:p-8 h-full flex flex-col">
                        <div className="bg-blue-50 w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 rounded-lg flex items-center justify-center mb-3 sm:mb-4 md:mb-6 group-hover:scale-110 transition-transform duration-300">
                          <service.icon className="h-5 w-5 sm:h-6 sm:w-6 md:h-8 md:w-8 text-[#2563EB]" />
                        </div>
                        <h3 className="text-base sm:text-lg md:text-xl font-bold text-[#0F172A] mb-2 sm:mb-2 md:mb-3 font-heading group-hover:text-[#2563EB] transition-colors leading-tight">
                          {service.title}
                        </h3>
                        <p className="text-xs sm:text-sm md:text-base text-slate-600 mb-3 sm:mb-3 md:mb-4 flex-grow">{service.description}</p>
                        <div className="flex items-center text-[#2563EB] font-medium text-xs sm:text-sm md:text-base mt-auto">
                          Learn More
                          <ArrowRight className="ml-2 h-3 w-3 sm:h-4 sm:w-4 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </CardContent>
                    </Link>
                  </Card>
                </div>
              ))}
            </Slider>
          </div>
        </div>
      </section>

      {/* Industry Recognition Section - NEW */}
      <section className="section-padding bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#0F172A] mb-4 font-heading">Industry Recognition</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Recognized by leading legal organizations for our excellence and commitment to client success
            </p>
          </div>
          
          {/* Mobile: Slider, Desktop: Grid */}
          <div className="block md:hidden max-w-5xl mx-auto">
            <Slider
              dots={true}
              infinite={true}
              speed={500}
              slidesToShow={1}
              slidesToScroll={1}
              arrows={false}
              autoplay={true}
              autoplaySpeed={3000}
            >
              <div className="px-4">
                <Card className="bg-white border-0 shadow-md hover:shadow-xl transition-shadow p-6 text-center">
                  <div className="bg-gradient-to-br from-blue-50 to-slate-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Award className="h-8 w-8 text-[#B45309]" />
                  </div>
                  <h3 className="font-bold text-[#0F172A] mb-2">Best Law Firm</h3>
                  <p className="text-sm text-slate-600">California 2024</p>
                </Card>
              </div>
              <div className="px-4">
                <Card className="bg-white border-0 shadow-md hover:shadow-xl transition-shadow p-6 text-center">
                  <div className="bg-gradient-to-br from-blue-50 to-slate-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Star className="h-8 w-8 text-[#B45309]" />
                  </div>
                  <h3 className="font-bold text-[#0F172A] mb-2">Top Rated</h3>
                  <p className="text-sm text-slate-600">Legal Excellence</p>
                </Card>
              </div>
              <div className="px-4">
                <Card className="bg-white border-0 shadow-md hover:shadow-xl transition-shadow p-6 text-center">
                  <div className="bg-gradient-to-br from-blue-50 to-slate-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <HandshakeIcon className="h-8 w-8 text-[#2563EB]" />
                  </div>
                  <h3 className="font-bold text-[#0F172A] mb-2">Client Choice</h3>
                  <p className="text-sm text-slate-600">Award 2024</p>
                </Card>
              </div>
              <div className="px-4">
                <Card className="bg-white border-0 shadow-md hover:shadow-xl transition-shadow p-6 text-center">
                  <div className="bg-gradient-to-br from-blue-50 to-slate-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="h-8 w-8 text-[#2563EB]" />
                  </div>
                  <h3 className="font-bold text-[#0F172A] mb-2">Certified</h3>
                  <p className="text-sm text-slate-600">ISO 9001:2015</p>
                </Card>
              </div>
            </Slider>
          </div>

          <div className="hidden md:grid md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            <Card className="bg-white border-0 shadow-md hover:shadow-xl transition-shadow p-6 text-center">
              <div className="bg-gradient-to-br from-blue-50 to-slate-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="h-8 w-8 text-[#B45309]" />
              </div>
              <h3 className="font-bold text-[#0F172A] mb-2">Best Law Firm</h3>
              <p className="text-sm text-slate-600">California 2024</p>
            </Card>
            <Card className="bg-white border-0 shadow-md hover:shadow-xl transition-shadow p-6 text-center">
              <div className="bg-gradient-to-br from-blue-50 to-slate-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="h-8 w-8 text-[#B45309]" />
              </div>
              <h3 className="font-bold text-[#0F172A] mb-2">Top Rated</h3>
              <p className="text-sm text-slate-600">Legal Excellence</p>
            </Card>
            <Card className="bg-white border-0 shadow-md hover:shadow-xl transition-shadow p-6 text-center">
              <div className="bg-gradient-to-br from-blue-50 to-slate-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <HandshakeIcon className="h-8 w-8 text-[#2563EB]" />
              </div>
              <h3 className="font-bold text-[#0F172A] mb-2">Client Choice</h3>
              <p className="text-sm text-slate-600">Award 2024</p>
            </Card>
            <Card className="bg-white border-0 shadow-md hover:shadow-xl transition-shadow p-6 text-center">
              <div className="bg-gradient-to-br from-blue-50 to-slate-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-[#2563EB]" />
              </div>
              <h3 className="font-bold text-[#0F172A] mb-2">Certified</h3>
              <p className="text-sm text-slate-600">ISO 9001:2015</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Practice Areas Overview - NEW */}
      <section className="section-padding bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-[#0F172A] mb-4 font-heading">Comprehensive Legal Coverage</h2>
              <p className="text-lg text-slate-600">
                From corporate law to intellectual property, we've got you covered
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-blue-50 to-slate-50 p-8 rounded-2xl">
                <h3 className="text-2xl font-bold text-[#0F172A] mb-6 font-heading">Enterprise Growth Solutions</h3>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-[#2563EB] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Strategic entity formation and operational restructuring</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-[#2563EB] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">High-stakes contract architecture and negotiation mastery</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-[#2563EB] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Value-maximizing mergers, acquisitions, and strategic alliances</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-[#2563EB] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Proactive regulatory navigation and corporate governance</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-[#2563EB] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Winning litigation strategy and alternative dispute resolution</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-gradient-to-br from-slate-50 to-blue-50 p-8 rounded-2xl">
                <h3 className="text-2xl font-bold text-[#0F172A] mb-6 font-heading">Premium Specialty Services</h3>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-[#2563EB] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Comprehensive brand portfolio and copyright shield</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-[#2563EB] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Streamlined immigration and global talent acquisition</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-[#2563EB] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Mission-aligned non-profit structuring and tax optimization</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-[#2563EB] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Strategic government contract pursuit and compliance</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-[#2563EB] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Enterprise anti-corruption frameworks and FCPA mastery</span>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="text-center mt-8">
              <Button
                onClick={() => setIsConsultationOpen(true)}
                size="lg"
                className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-8 py-6"
              >
                Discuss Your Legal Needs
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-padding bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#0F172A] mb-4 font-heading">How It Works</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Four simple steps to get the legal help you need
            </p>
          </div>
          {/* Mobile: Slider, Desktop: Grid */}
          <div className="block md:hidden">
            <Slider
              dots={true}
              infinite={true}
              speed={500}
              slidesToShow={1}
              slidesToScroll={1}
              arrows={false}
              autoplay={true}
              autoplaySpeed={3000}
            >
              {howItWorks.map((item, index) => (
                <div key={index} className="px-4">
                  <div className="text-center">
                    <div className="bg-gradient-to-br from-blue-50 to-slate-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 shadow-md">
                      <span className="text-3xl font-bold text-[#2563EB]">{item.step}</span>
                    </div>
                    <h3 className="text-xl font-bold text-[#0F172A] mb-3 font-heading">{item.title}</h3>
                    <p className="text-slate-600">{item.description}</p>
                  </div>
                </div>
              ))}
            </Slider>
          </div>

          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorks.map((item, index) => (
              <div key={index} className="text-center">
                <div className="bg-gradient-to-br from-blue-50 to-slate-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 shadow-md">
                  <span className="text-3xl font-bold text-[#2563EB]">{item.step}</span>
                </div>
                <h3 className="text-xl font-bold text-[#0F172A] mb-3 font-heading">{item.title}</h3>
                <p className="text-slate-600">{item.description}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Button
              onClick={() => setIsConsultationOpen(true)}
              size="lg"
              className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-8 py-6"
              data-testid="how-it-works-cta"
            >
              Start Your Free Consultation
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-white hidden lg:block">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-[#0F172A] mb-6 font-heading">Why Leading Organizations Choose Zorvian Associates</h2>
              <p className="text-lg text-slate-600 mb-8">
                We fuse decades of courtroom victories with innovative business strategy to consistently exceed expectations.
              </p>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-blue-50 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield className="h-6 w-6 text-[#2563EB]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#0F172A] mb-2">Documented Excellence</h3>
                    <p className="text-slate-600">Five hundred successful engagements with ninety-eight percent client retention.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-blue-50 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="h-6 w-6 text-[#2563EB]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#0F172A] mb-2">Urgent Response Protocol</h3>
                    <p className="text-slate-600">Direct attorney access guaranteed within twenty-four hours of engagement request.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-blue-50 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Scale className="h-6 w-6 text-[#2563EB]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#0F172A] mb-2">Customized Solutions</h3>
                    <p className="text-slate-600">Bespoke legal frameworks precision-engineered for your specific business context.</p>
                  </div>
                </div>
              </div>
            </div>
            {/* Mobile: Slider, Desktop: Grid */}
            <div className="block md:hidden">
              <Slider
                dots={true}
                infinite={true}
                speed={500}
                slidesToShow={1}
                slidesToScroll={1}
                arrows={false}
                autoplay={true}
                autoplaySpeed={3000}
              >
                <div className="px-4">
                  <Card className="bg-white shadow-lg border-0 p-6 hover:shadow-xl transition-shadow">
                    <Award className="h-8 w-8 text-[#B45309] mb-4" />
                    <h4 className="font-bold text-[#0F172A] mb-2">AV Rated</h4>
                    <p className="text-sm text-slate-600">Highest peer rating standard</p>
                  </Card>
                </div>
                <div className="px-4">
                  <Card className="bg-white shadow-lg border-0 p-6 hover:shadow-xl transition-shadow">
                    <Shield className="h-8 w-8 text-[#2563EB] mb-4" />
                    <h4 className="font-bold text-[#0F172A] mb-2">Licensed</h4>
                    <p className="text-sm text-slate-600">All 50 states</p>
                  </Card>
                </div>
                <div className="px-4">
                  <Card className="bg-white shadow-lg border-0 p-6 hover:shadow-xl transition-shadow">
                    <Users className="h-8 w-8 text-[#2563EB] mb-4" />
                    <h4 className="font-bold text-[#0F172A] mb-2">15+ Attorneys</h4>
                    <p className="text-sm text-slate-600">Expert legal team</p>
                  </Card>
                </div>
                <div className="px-4">
                  <Card className="bg-white shadow-lg border-0 p-6 hover:shadow-xl transition-shadow">
                    <CheckCircle className="h-8 w-8 text-[#B45309] mb-4" />
                    <h4 className="font-bold text-[#0F172A] mb-2">500+ Cases</h4>
                    <p className="text-sm text-slate-600">Successfully resolved</p>
                  </Card>
                </div>
              </Slider>
            </div>
            
            <div className="hidden md:grid grid-cols-2 gap-4">
              <Card className="bg-white shadow-lg border-0 p-6 hover:shadow-xl transition-shadow">
                <Award className="h-8 w-8 text-[#B45309] mb-4" />
                <h4 className="font-bold text-[#0F172A] mb-2">AV Rated</h4>
                <p className="text-sm text-slate-600">Highest peer rating standard</p>
              </Card>
              <Card className="bg-white shadow-lg border-0 p-6 hover:shadow-xl transition-shadow">
                <Shield className="h-8 w-8 text-[#2563EB] mb-4" />
                <h4 className="font-bold text-[#0F172A] mb-2">Licensed</h4>
                <p className="text-sm text-slate-600">All 50 states</p>
              </Card>
              <Card className="bg-white shadow-lg border-0 p-6 hover:shadow-xl transition-shadow">
                <Users className="h-8 w-8 text-[#2563EB] mb-4" />
                <h4 className="font-bold text-[#0F172A] mb-2">15+ Attorneys</h4>
                <p className="text-sm text-slate-600">Expert legal team</p>
              </Card>
              <Card className="bg-white shadow-lg border-0 p-6 hover:shadow-xl transition-shadow">
                <CheckCircle className="h-8 w-8 text-[#B45309] mb-4" />
                <h4 className="font-bold text-[#0F172A] mb-2">500+ Cases</h4>
                <p className="text-sm text-slate-600">Successfully resolved</p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Our Commitment */}
      <section className="section-padding bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#0F172A] mb-4 font-heading">Our Commitment to Excellence</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              The core values that guide our legal practice and client relationships
            </p>
          </div>
          {/* Mobile: Slider, Desktop: Grid */}
          <div className="block md:hidden">
            <Slider
              dots={true}
              infinite={true}
              speed={500}
              slidesToShow={1}
              slidesToScroll={1}
              arrows={false}
              autoplay={true}
              autoplaySpeed={3000}
            >
              <div className="px-4">
                <Card className="bg-white border-0 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                  <CardContent className="p-8 text-center">
                    <div className="bg-gradient-to-br from-blue-50 to-slate-50 w-20 h-20 rounded-xl flex items-center justify-center mx-auto mb-6">
                      <Shield className="h-10 w-10 text-[#2563EB]" />
                    </div>
                    <h3 className="text-xl font-bold text-[#0F172A] mb-4 font-heading">Integrity First</h3>
                    <p className="text-slate-600 leading-relaxed">
                      We uphold the highest ethical standards in every case, ensuring transparent communication and honest counsel at all times.
                    </p>
                  </CardContent>
                </Card>
              </div>
              <div className="px-4">
                <Card className="bg-white border-0 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                  <CardContent className="p-8 text-center">
                    <div className="bg-gradient-to-br from-blue-50 to-slate-50 w-20 h-20 rounded-xl flex items-center justify-center mx-auto mb-6">
                      <Users className="h-10 w-10 text-[#2563EB]" />
                    </div>
                    <h3 className="text-xl font-bold text-[#0F172A] mb-4 font-heading">Client-Focused</h3>
                    <p className="text-slate-600 leading-relaxed">
                      Your goals are our priority. We tailor our legal strategies to your unique needs and maintain responsive, personalized service.
                    </p>
                  </CardContent>
                </Card>
              </div>
              <div className="px-4">
                <Card className="bg-white border-0 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                  <CardContent className="p-8 text-center">
                    <div className="bg-gradient-to-br from-blue-50 to-slate-50 w-20 h-20 rounded-xl flex items-center justify-center mx-auto mb-6">
                      <Award className="h-10 w-10 text-[#2563EB]" />
                    </div>
                    <h3 className="text-xl font-bold text-[#0F172A] mb-4 font-heading">Proven Excellence</h3>
                    <p className="text-slate-600 leading-relaxed">
                      Our track record speaks for itself. We combine deep legal expertise with innovative strategies to achieve outstanding results.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </Slider>
          </div>

          <div className="hidden md:grid md:grid-cols-3 gap-8">
            <Card className="bg-white border-0 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <CardContent className="p-8 text-center">
                <div className="bg-gradient-to-br from-blue-50 to-slate-50 w-20 h-20 rounded-xl flex items-center justify-center mx-auto mb-6">
                  <Shield className="h-10 w-10 text-[#2563EB]" />
                </div>
                <h3 className="text-xl font-bold text-[#0F172A] mb-4 font-heading">Integrity First</h3>
                <p className="text-slate-600 leading-relaxed">
                  We uphold the highest ethical standards in every case, ensuring transparent communication and honest counsel at all times.
                </p>
              </CardContent>
            </Card>
            <Card className="bg-white border-0 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <CardContent className="p-8 text-center">
                <div className="bg-gradient-to-br from-blue-50 to-slate-50 w-20 h-20 rounded-xl flex items-center justify-center mx-auto mb-6">
                  <Users className="h-10 w-10 text-[#2563EB]" />
                </div>
                <h3 className="text-xl font-bold text-[#0F172A] mb-4 font-heading">Client-Focused</h3>
                <p className="text-slate-600 leading-relaxed">
                  Your goals are our priority. We tailor our legal strategies to your unique needs and maintain responsive, personalized service.
                </p>
              </CardContent>
            </Card>
            <Card className="bg-white border-0 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <CardContent className="p-8 text-center">
                <div className="bg-gradient-to-br from-blue-50 to-slate-50 w-20 h-20 rounded-xl flex items-center justify-center mx-auto mb-6">
                  <Award className="h-10 w-10 text-[#2563EB]" />
                </div>
                <h3 className="text-xl font-bold text-[#0F172A] mb-4 font-heading">Proven Excellence</h3>
                <p className="text-slate-600 leading-relaxed">
                  Our track record speaks for itself. We combine deep legal expertise with innovative strategies to achieve outstanding results.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-[#0F172A] mb-4 font-heading">Frequently Asked Questions</h2>
              <p className="text-lg text-slate-600">
                Get answers to common questions about our legal services
              </p>
            </div>
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="bg-slate-50 border-0 shadow-sm rounded-lg px-6">
                  <AccordionTrigger className="text-left font-semibold text-[#0F172A] hover:text-[#2563EB]">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-br from-[#0F172A] via-[#1e293b] to-[#0F172A] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl font-bold mb-6 font-heading">Ready to Get Started?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-slate-300">
            Don't navigate complex legal matters alone. Let our experienced attorneys guide you to success.
          </p>
          <Button
            onClick={() => setIsConsultationOpen(true)}
            size="lg"
            className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-8 py-6 text-lg shadow-xl shadow-blue-500/20"
            data-testid="footer-cta-button"
          >
            Request Free Consultation Now
          </Button>
        </div>
      </section>

      <ConsultationDialog open={isConsultationOpen} onOpenChange={setIsConsultationOpen} />
    </div>
  );
};

export default HomePage;
